import { MessageSquareShare, Search } from 'lucide-react';
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from '../../DataTablePagination';
import Spacing from '../../Spacing';
import BulkLetterDataTable from '../../DataTable/BulkLetterDataTable';

import { debounce, generateLetterTableData, getPractice, numberWithCommas } from "../../../helpers";
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import { fetchletter } from '../../../services/letter';

export default function BulkLetters({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [messageType, setMessageType] = useState([]);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    message_type: "",
    order_by: [],
  });

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);

  const loadTemplate = async () => {
    const response = await fetchletter(
      {
        page: 1,
        per_page: 10,
        search_term: "",
        message_type: "",
        order_by: [],
      },
      account_id
    );
    if (response) {
      const uniqueMessageTypes = Array.from(
        new Set(response?.data?.results?.map((type) => type?.notification_type))
      );
      const uniqueResults = uniqueMessageTypes?.map((notification_type) => ({
        notification_type,
      }));

      setMessageType(uniqueResults);
    }
  };


  useEffect(() => {
    loadData(filter);
    loadTemplate();
  }, [filter]);


  // Loads data based on the provided filter
  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchletter(getFilter, account_id);
      if (response) {
        const generateData = generateLetterTableData(response?.data?.results);
        setData(generateData);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the checkbox change event for a specific patient.
  const handleCheckboxChange = (id) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter(checkedId => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  // Handles the select all functionality for data.
  const handleSelectAll = () => {
    const newCheckedIds = selectAll
      ? []
      : data.map(item => item.id);
    setCheckedIds(newCheckedIds);
    
    const newData = data.map((row) => ({
      ...row,
      checked: !selectAll,
    }));   
    setData(newData);
    setSelectAll(!selectAll);
  };


  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };


  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {    
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const filterByType = async (e) => {
    setFilter({ ...filter, message_type: e.target.value });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
  }, [location_id, account_id]);

  useDocumentTitle("Letter Transaction");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#898E87" }}
          >
            <MessageSquareShare width={20} height={20} />
          </i>
          Letter Transaction
          <span>{numberWithCommas(dataCount?.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="docpro_data_table_heading_input_group">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>
          <select
            className="docpro_form_field"
            onChange={filterByType}
            style={{ width: "auto", minWidth: "240px" }}
          >
            <option value="">All</option>
            {messageType?.map((type, index) => (
              <option key={index} value={type.notification_type}>
                {type.notification_type}
              </option>
            ))}
          </select>
        </div>
        <BulkLetterDataTable
          tableData={data}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {(data?.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount?.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount?.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}